/**
 * Subscriber Service
 * 
 * Service for managing newsletter subscribers in Firestore.
 * Prepared for future newsletter functionality.
 */

import { 
  Subscriber, 
  SubscriberCreateInput, 
  SubscriberStatus, 
  SubscriberSource,
  SUBSCRIBERS_COLLECTION 
} from '../types/newsletter';
import { 
  getDocument, 
  createDocument, 
  updateDocument, 
  deleteDocument, 
  queryCollection,
  getCollection 
} from './firestore/firestoreService';

class SubscriberService {
  /**
   * Create a new subscriber
   */
  async createSubscriber(data: SubscriberCreateInput): Promise<Subscriber> {
    // Check for existing subscriber with same email
    const existing = await this.getSubscriberByEmail(data.email);
    if (existing) {
      throw new Error('El correo electrónico ya está registrado');
    }

    const now = new Date().toISOString();
    const subscriberData = {
      email: data.email,
      name: data.name || '',
      status: SubscriberStatus.ACTIVE,
      subscribedAt: now,
      source: data.source || SubscriberSource.WEBSITE,
      preferences: data.preferences || {},
    };

    const result = await createDocument(SUBSCRIBERS_COLLECTION, subscriberData);
    if (!result.success || !result.id) {
      throw new Error(result.error || 'No se pudo registrar la suscripción');
    }

    return {
      id: result.id,
      ...subscriberData,
    };
  }

  /**
   * Get subscriber by ID
   */
  async getSubscriberById(id: string): Promise<Subscriber | null> {
    // @ts-ignore - Firestore service return type mismatch
    const result = await getDocument<Subscriber>(SUBSCRIBERS_COLLECTION, id);
    return result.data || null;
  }

  /**
   * Get subscriber by email
   */
  async getSubscriberByEmail(email: string): Promise<Subscriber | null> {
    // @ts-ignore - Firestore service return type mismatch
    const result = await queryCollection<Subscriber>(SUBSCRIBERS_COLLECTION, [
      { field: 'email', operator: '==', value: email },
    ]);
    return result.data && result.data.length > 0 ? result.data[0] : null;
  }

  /**
   * Get all subscribers
   */
  async getAllSubscribers(): Promise<Subscriber[]> {
    // @ts-ignore - Firestore service return type mismatch
    const result = await getCollection<Subscriber>(SUBSCRIBERS_COLLECTION);
    return result.data || [];
  }

  /**
   * Get all active subscribers
   */
  async getActiveSubscribers(): Promise<Subscriber[]> {
    // @ts-ignore - Firestore service return type mismatch
    const result = await queryCollection<Subscriber>(SUBSCRIBERS_COLLECTION, [
      { field: 'status', operator: '==', value: SubscriberStatus.ACTIVE },
    ]);
    return result.data || [];
  }

  /**
   * Update subscriber
   */
  async updateSubscriber(id: string, data: Partial<Subscriber>): Promise<Subscriber | null> {
    const subscriber = await this.getSubscriberById(id);
    if (!subscriber) return null;

    await updateDocument(SUBSCRIBERS_COLLECTION, id, data);
    return { ...subscriber, ...data };
  }

  /**
   * Unsubscribe a subscriber
   */
  async unsubscribe(id: string): Promise<Subscriber | null> {
    return this.updateSubscriber(id, {
      status: SubscriberStatus.UNSUBSCRIBED,
      unsubscribedAt: new Date().toISOString(),
    });
  }

  /**
   * Delete subscriber (hard delete)
   */
  async deleteSubscriber(id: string): Promise<boolean> {
    try {
      await deleteDocument(SUBSCRIBERS_COLLECTION, id);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get subscriber count by status
   */
  async getSubscriberCountByStatus(): Promise<Record<SubscriberStatus, number>> {
    // @ts-ignore - Firestore service return type mismatch
    const result = await getCollection<Subscriber>(SUBSCRIBERS_COLLECTION);
    const subscribers = result.data || [];
    const counts: Record<SubscriberStatus, number> = {
      [SubscriberStatus.ACTIVE]: 0,
      [SubscriberStatus.UNSUBSCRIBED]: 0,
      [SubscriberStatus.PENDING]: 0,
      [SubscriberStatus.BOUNCED]: 0,
    };

    subscribers.forEach(subscriber => {
      counts[subscriber.status]++;
    });

    return counts;
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export const subscriberService = new SubscriberService();
export default SubscriberService;